/**
 * PipelinesPage — Heroku-style list of deployed workflows for a project.
 *
 * Mounted at /projects/:projectId/pipelines as a child of ProjectLayout
 * (so the sidebar + header + project context come from the layout). Just
 * the list content lives here.
 *
 * Data: GET /projects/<projectId>/workflows returns minimal metadata per
 * workflow (workflowType, uuid, version, updatedAt, createdAt).
 * Click a row → /projects/<projectId>/pipelines/<workflowType>.
 *
 * Reuses DataTable (the same component TokensList uses) so the look
 * matches the rest of the dashboard. No new components.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { copyOutline, globeOutline } from 'ionicons/icons';
import { IonIcon } from '@ionic/react';
import DataTable from '../../components/shared/DataTable/DataTable';
import WorkflowDemo from '../../components/shared/GetStartedSteps/WorkflowDemo';
import PipelineStatus from './PipelineStatus';
import api from '../../services/api';
import { timeAgo } from '../../utils/timeAgo';
import './PipelinesPage.css';

const PipelinesPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { project, setToast } = useOutletContext() || {};
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  // Static egress IP — shown only if (a) the account has the dedicated
  // egress addon active AND (b) this project opted in. Account-scoped
  // status endpoint also returns the per-project usesDedicatedEgress flag.
  const [egressIp, setEgressIp] = useState(null);

  const fetchPipelines = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/projects/${projectId}/workflows`);
      // Normalize so DataTable's `rowKey="workflowType"` resolves cleanly
      // (every record already has workflowType from backend).
      setPipelines(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch pipelines:', err);
      setToast?.({ isOpen: true, message: `Could not load pipelines: ${err.message}`, color: 'danger' });
      setPipelines([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, setToast]);

  useEffect(() => {
    // Best-effort egress lookup. Two independent reads:
    //   (a) GET /addons/dedicated-egress/status   — does the account have
    //       the addon active + an EIP allocated?
    //   (b) GET /projects/<id>                    — does THIS project opt
    //       into using the EIP?
    //
    // We read them separately rather than relying on the joined
    // `status.projects[]` because the dedicated-egress handler's account
    // lookup uses principalId-as-accountId, while the projects table has
    // a different accountId field — the join silently returns empty in
    // that case. Reading the project record directly avoids the join
    // mismatch entirely.
    //
    // Failures (no addon, free plan, network) are silently ignored — the
    // pill just doesn't appear. Don't block the list on this.
    (async () => {
      try {
        const [status, projResp] = await Promise.all([
          api.get('/addons/dedicated-egress/status'),
          api.get(`/projects/${projectId}`),
        ]);
        const proj = projResp?.project || projResp;
        const isActive = status?.enabled && status?.status === 'active' && status?.elasticIp;
        const optedIn  = !!proj?.usesDedicatedEgress;
        if (isActive && optedIn) setEgressIp(status.elasticIp);
      } catch { /* silent — pill just doesn't render */ }
    })();
    fetchPipelines();
  }, [fetchPipelines]);

  // ── columns ────────────────────────────────────────────────────────
  // 4 columns deliberately. STATUS is derived (we don't have bundleStatus
  // in the list response — server returns it on the detail endpoint). For
  // now: any deployed workflow with version >= 1 is "Ready". Builds in
  // progress show on the detail page where we have richer data.
  const columns = [
    {
      header: 'NAME',
      key: 'workflowType',
      className: 'pipelines-col-name',
      render: (p) => (
        <span className="pipelines-name-cell">
          <span className="pipelines-name">{p.workflowType}</span>
        </span>
      ),
    },
    {
      header: 'STATUS',
      key: 'status',
      className: 'pipelines-col-status',
      // Shared <PipelineStatus> atom — same component renders this
      // cell AND the detail-page header pill so the two views can
      // never drift. See PipelineStatus.js for the resolution logic.
      render: (p) => (
        <PipelineStatus
          bundleStatus={p.bundleStatus}
          warmEnabled={p.warmEnabled}
          variant="table"
        />
      ),
    },
    {
      header: 'VERSION',
      key: 'version',
      className: 'pipelines-col-version',
      render: (p) => <span className="pipelines-version">v{p.version || 0}</span>,
    },
    {
      header: 'LAST DEPLOYED',
      key: 'updatedAt',
      className: 'pipelines-col-deployed',
      render: (p) => <span className="pipelines-deployed">{p.updatedAt ? timeAgo(p.updatedAt) : '—'}</span>,
    },
  ];

  // Pipeline row actions — text-only menu items (no icons) so the menu
  // reads as a clean list. "Activities" matches the detail page's
  // Activity tab label.
  const actions = [
    {
      label: 'View details',
      onClick: (p) => navigate(`/projects/${projectId}/pipelines/${p.workflowType}`),
    },
    {
      label: 'Activities',
      onClick: (p) => navigate(`/projects/${projectId}/pipelines/${p.workflowType}?tab=activity`),
    },
    { divider: true },
    {
      label: 'Delete',
      danger: true,
      onClick: async (p) => {
        if (!window.confirm(`Delete pipeline "${p.workflowType}"? This cannot be undone.`)) return;
        if (!p.uuid) {
          setToast?.({ isOpen: true, message: 'Cannot delete: workflow has no UUID yet (deploy it first)', color: 'danger' });
          return;
        }
        try {
          // Backend wires DELETE on `/workflows/{uuid}`, NOT
          // `/projects/{id}/workflows/{type}`. The handler looks the
          // workflow up via the UUID GSI then verifies the project
          // membership server-side, so the projectId is implied.
          await api.delete(`/workflows/${p.uuid}`);
          // Optimistic local removal — no `fetchPipelines()` here. A
          // refetch flips loading=true, which makes DataTable swap the
          // whole tbody for skeletons for ~one paint and the table
          // visibly blinks. We already know exactly what changed; drop
          // the row from state instead.
          setPipelines((prev) => prev.filter((x) => x.uuid !== p.uuid));
          setToast?.({ isOpen: true, message: `Pipeline "${p.workflowType}" deleted`, color: 'success' });
        } catch (err) {
          setToast?.({ isOpen: true, message: `Delete failed: ${err.message}`, color: 'danger' });
        }
      },
    },
  ];

  return (
    <>
      <Helmet>
        <title>Pipelines · Zibby</title>
      </Helmet>
      {/* When empty: return ONLY the .pipelines-empty block — no outer
          .pipelines-page wrapper. Test Runs does the same so we match
          padding/positioning exactly. The .pipelines-page wrapper (with
          24px 32px padding) only fires when there's a list to render. */}
      {!loading && pipelines.length === 0 ? (
        <div className="pipelines-empty">
          <WorkflowDemo />
        </div>
      ) : (
        <div className="pipelines-page">
          {!loading && pipelines.length > 0 && (
            <div className="pipelines-header">
              {/* Title row — page title on the left, optional static-egress
                  pill on the right. Pill only renders when the project has
                  the paid dedicated-egress addon active AND has opted in.
                  Click the pill to copy the IP. */}
              <div className="pipelines-header-row">
                <h1 className="pipelines-title">Pipelines</h1>
                {egressIp && (
                  <button
                    type="button"
                    className="pipelines-egress-pill"
                    onClick={() => {
                      navigator.clipboard?.writeText(egressIp).then(() => {
                        setToast?.({ isOpen: true, message: `Copied ${egressIp}`, color: 'success' });
                      });
                    }}
                    title="Copy static egress IP"
                  >
                    <IonIcon icon={globeOutline} className="pipelines-egress-pill-icon" />
                    <span className="pipelines-egress-pill-label">Static egress</span>
                    <code className="pipelines-egress-pill-ip">{egressIp}</code>
                    <IonIcon icon={copyOutline} className="pipelines-egress-pill-copy" />
                  </button>
                )}
              </div>
              <p className="pipelines-subtitle">
                Deployed agent workflows for this project. Trigger them via the CLI or webhook.
              </p>
            </div>
          )}

          <DataTable
            data={pipelines}
            columns={columns}
            loading={loading}
            rowKey="workflowType"
            actions={actions}
            onRowClick={(p) => navigate(`/projects/${projectId}/pipelines/${p.workflowType}`)}
          />
        </div>
      )}
    </>
  );
};

export default PipelinesPage;
